import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Swiper } from 'swiper';
import { debounce } from '@helpers/utils/general';
import { updateAriaLiveAnnouncement } from './utils/aria-live-announcement';
import { updateSlideAccessibility } from './utils/slide-accessibility';

type UseSwiperAccessibilityOptions = {
  getSlideLabel?: (currentSlide: number, totalSlides: number) => string;
  getAriaLiveLabel?: (startSlide: number, endSlide: number, totalSlides: number) => string;
  swiper?: Swiper | null;
  slidesPerView?: number;
  activeIndex?: number; // The calling component is responsible for managing the current slide index.
  slides?: unknown[];
  useInert?: boolean;
  onViewportChange?: (isInViewport: boolean) => void; // Callback when viewport visibility changes
};

export const useSwiperAccessibility = ({
  getSlideLabel,
  getAriaLiveLabel,
  swiper,
  slidesPerView,
  activeIndex,
  slides,
  useInert = false,
  onViewportChange,
}: UseSwiperAccessibilityOptions) => {
  const ariaLiveRef = useRef<HTMLDivElement>(null);
  const isInViewportRef = useRef(false);

  // Store prop values in refs to avoid effect re-runs
  const propsRef = useRef({
    activeIndex,
    slides,
    slidesPerView,
    useInert,
    getSlideLabel,
    getAriaLiveLabel,
    onViewportChange,
  });

  // Update ref during render to always have latest values
  propsRef.current = {
    activeIndex,
    slides,
    slidesPerView,
    useInert,
    getSlideLabel,
    getAriaLiveLabel,
    onViewportChange,
  };

  const updateAccessibility = useCallback(() => {
    if (!swiper?.slides || !ariaLiveRef.current) {
      return;
    }

    const props = propsRef.current;
    updateAriaLiveAnnouncement({
      ariaLiveRef,
      swiper,
      slidesPerView: props.slidesPerView,
      activeIndex: props.activeIndex,
      getAriaLiveLabel: props.getAriaLiveLabel,
    });

    updateSlideAccessibility({
      swiper,
      slides: props.slides,
      useInert: props.useInert,
      getSlideLabel: props.getSlideLabel,
    });
  }, [swiper]);

  const debouncedUpdate = useMemo(() => debounce(updateAccessibility, 150), [updateAccessibility]);

  useEffect(() => {
    if (!swiper?.on || !swiper?.off) {
      return;
    }
    const onInit = () => updateAccessibility();
    const onResize = () => debouncedUpdate();
    const onSlideChange = () => debouncedUpdate();
    const onTransitionEnd = () => debouncedUpdate();

    // Set initial state
    const initTimer = setTimeout(() => updateAccessibility(), 100);
    swiper.on('afterInit', onInit);

    // Viewport-aware listeners
    const el = swiper.el as HTMLElement;
    let observer: IntersectionObserver | null = null;

    if (el && typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver(
        ([entry]) => {
          const wasVisible = isInViewportRef.current;
          isInViewportRef.current = entry.isIntersecting;

          if (entry.isIntersecting && !wasVisible) {
            // Entered viewport: Attach listeners and notify component
            swiper.on('slideChange', onSlideChange);
            swiper.on('transitionEnd', onTransitionEnd);
            swiper.on('resize', onResize);
            updateAccessibility();
            // Use latest callback from propsRef
            propsRef.current.onViewportChange?.(true);
          } else if (!entry.isIntersecting && wasVisible) {
            // Left viewport: Detach listeners and notify component
            swiper.off('slideChange', onSlideChange);
            swiper.off('transitionEnd', onTransitionEnd);
            swiper.off('resize', onResize);
            // Use latest callback from propsRef
            propsRef.current.onViewportChange?.(false);
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(el);
    } else {
      // Fallback for old browsers/SSR: Always listen (no viewport optimization)
      swiper.on('slideChange', onSlideChange);
      swiper.on('transitionEnd', onTransitionEnd);
      swiper.on('resize', onResize);
    }

    return () => {
      clearTimeout(initTimer);
      // Cancel any pending debounced calls using the exposed cancel method
      debouncedUpdate.cancel();
      observer?.disconnect();
      swiper.off?.('afterInit', onInit);
      swiper.off?.('slideChange', onSlideChange);
      swiper.off?.('transitionEnd', onTransitionEnd);
      swiper.off?.('resize', onResize);
    };
  }, [swiper, updateAccessibility, debouncedUpdate]);

  return { ariaLiveRef, isInViewportRef };
};

export default useSwiperAccessibility;
